import React, { useContext, useState, useEffect } from 'react'
import { X, Download } from 'lucide-react'
import { pdf, Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer'
import ModalContext from '../../../contexts/ModalContext'
import { OccurrenceContext } from '../../../contexts/OccurrenceContext'
import { FeedbackContext } from '../../../contexts/FeedbackContext'
import ApiService from '../../../services/ApiService'
import { Occurrence } from '../../../pages/ClientPanel/Occurrences/OccurencesTable/types'
import logoDustre from '../../../assets/Cert/logo-dustre-cert.png'
import {
    Container,
    Header,
    TitleRow,
    CloseButton,
    InfosContainer,
    Section,
    SectionDivider,
    SectionSubtitle,
    InfoGrid,
    InfoItem,
    InfoLabel,
    InfoValue,
    TextBlock,
    TextBlockLabel,
    SimpleTable,
    SimpleTableRow,
    TableHeader,
    TableCell,
    RelevantBadge,
    ButtonContainer,
    ButtonDiv,
    ConfirmOverlay,
    ConfirmCard,
    ConfirmTitle,
    ConfirmText,
    ConfirmOption,
    ConfirmActions,
} from './style'

const pdfStyles = StyleSheet.create({
    page: {
        paddingTop: 28,
        paddingHorizontal: 28,
        paddingBottom: 24,
        fontSize: 10,
        color: '#1F1F1F',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottomWidth: 1,
        borderBottomColor: '#EAEAEA',
        paddingBottom: 10,
        marginBottom: 16,
    },
    logo: {
        width: 110,
        height: 40,
        objectFit: 'contain',
    },
    title: {
        fontSize: 16,
        fontWeight: 700,
    },
    subtitle: {
        fontSize: 11,
        color: '#595959',
        marginTop: 2,
    },
    section: {
        marginBottom: 12,
    },
    sectionTitle: {
        fontSize: 11,
        fontWeight: 700,
        marginBottom: 6,
    },
    row: {
        flexDirection: 'row',
        marginBottom: 4,
    },
    label: {
        width: 120,
        fontWeight: 700,
        color: '#595959',
    },
    value: {
        flex: 1,
    },
    textBlock: {
        borderWidth: 1,
        borderColor: '#ECECEC',
        borderRadius: 4,
        padding: 8,
        lineHeight: 1.4,
    },
    listItem: {
        marginBottom: 6,
        lineHeight: 1.4,
    },
    attachmentPageTitle: {
        fontSize: 14,
        fontWeight: 700,
        marginBottom: 12,
    },
    attachmentImage: {
        width: '100%',
        height: 680,
        objectFit: 'contain',
    },
})

const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    try {
        const date = new Date(dateStr)
        return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' })
    } catch {
        return dateStr
    }
}

const blobToDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onloadend = () => resolve(reader.result as string)
        reader.onerror = () => reject(new Error('FileReader failed'))
        reader.readAsDataURL(blob)
    })

/** Quando Content-Type vem vazio ou application/octet-stream (comum no S3). */
const detectImageMimeFromBytes = (bytes: Uint8Array): string | null => {
    if (bytes.length < 12) return null
    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return 'image/png'
    if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg'
    if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) return 'image/gif'
    if (
        bytes[0] === 0x52 &&
        bytes[1] === 0x49 &&
        bytes[2] === 0x46 &&
        bytes[3] === 0x46 &&
        bytes[8] === 0x57 &&
        bytes[9] === 0x45 &&
        bytes[10] === 0x42 &&
        bytes[11] === 0x50
    ) {
        return 'image/webp'
    }
    return null
}

const bytesFromBase64Prefix = (b64: string, maxDecodedBytes: number): Uint8Array | null => {
    const clean = b64.replace(/\s/g, '')
    const sliceLen = Math.min(clean.length, Math.ceil((maxDecodedBytes * 4) / 3) + 8)
    try {
        const binary = atob(clean.slice(0, sliceLen))
        const len = Math.min(binary.length, maxDecodedBytes)
        const out = new Uint8Array(len)
        for (let i = 0; i < len; i++) out[i] = binary.charCodeAt(i)
        return out
    } catch {
        return null
    }
}

const resolveImageDataUrlFromApiContent = (
    mime: string,
    b64: string | undefined
): string | undefined => {
    if (!b64) return undefined
    let effective = mime || ''
    if (!effective.startsWith('image/')) {
        const prefix = bytesFromBase64Prefix(b64, 32)
        const sniffed = prefix ? detectImageMimeFromBytes(prefix) : null
        if (sniffed) effective = sniffed
    }
    if (!effective.startsWith('image/')) return undefined
    return `data:${effective};base64,${b64}`
}

/**
 * URLs assinadas (S3/GCS) costumam falhar no <Image> do @react-pdf/renderer.
 * Baixamos o blob no browser e embutimos como data URL no PDF.
 */
const tryFetchImageAsDataUrl = async (url: string): Promise<string | null> => {
    if (!url) return null
    try {
        const res = await fetch(url, { mode: 'cors', credentials: 'omit', cache: 'no-store' })
        if (!res.ok) return null
        const blob = await res.blob()
        if (blob.type.startsWith('image/')) {
            return await blobToDataUrl(blob)
        }
        const buf = new Uint8Array(await blob.arrayBuffer())
        const sniffed = detectImageMimeFromBytes(buf)
        if (sniffed) {
            return await blobToDataUrl(new Blob([buf], { type: sniffed }))
        }
        return null
    } catch {
        return null
    }
}

const OccurrencePDFDocument: React.FC<{
    data: Occurrence
    providences: Array<{ description: string; responsibleName: string | null; deadline: string }>
    files: Array<{ id: number; name: string; url?: string; imageDataUrl?: string }>
}> = ({ data, providences, files }) => (
    <Document>
        <Page size="A4" style={pdfStyles.page}>
            <View style={pdfStyles.header}>
                <View>
                    <Image src={logoDustre} style={pdfStyles.logo} />
                    <Text style={pdfStyles.subtitle}>Aços e Ligas Especiais</Text>
                </View>
                <View>
                    <Text style={pdfStyles.title}>Ocorrência #{data.id}</Text>
                    <Text style={pdfStyles.subtitle}>Documento gerado automaticamente</Text>
                </View>
            </View>

            <View style={pdfStyles.section}>
                <Text style={pdfStyles.sectionTitle}>Informações gerais</Text>
                <View style={pdfStyles.row}>
                    <Text style={pdfStyles.label}>Relevante:</Text>
                    <Text style={pdfStyles.value}>{data.relevant ? 'Sim' : 'Nao'}</Text>
                </View>
                <View style={pdfStyles.row}>
                    <Text style={pdfStyles.label}>OP:</Text>
                    <Text style={pdfStyles.value}>{data.operationNumber ?? 'N/A'}</Text>
                </View>
                <View style={pdfStyles.row}>
                    <Text style={pdfStyles.label}>Cliente:</Text>
                    <Text style={pdfStyles.value}>{data.companyName || data.contactName || 'N/A'}</Text>
                </View>
                <View style={pdfStyles.row}>
                    <Text style={pdfStyles.label}>Área:</Text>
                    <Text style={pdfStyles.value}>{data.area ?? 'N/A'}</Text>
                </View>
                <View style={pdfStyles.row}>
                    <Text style={pdfStyles.label}>Setor:</Text>
                    <Text style={pdfStyles.value}>{data.sector ?? 'N/A'}</Text>
                </View>
                <View style={pdfStyles.row}>
                    <Text style={pdfStyles.label}>Alocação:</Text>
                    <Text style={pdfStyles.value}>{data.allocation ?? 'N/A'}</Text>
                </View>
                <View style={pdfStyles.row}>
                    <Text style={pdfStyles.label}>Etapa:</Text>
                    <Text style={pdfStyles.value}>{data.Stage?.name ?? 'N/A'}</Text>
                </View>
                <View style={pdfStyles.row}>
                    <Text style={pdfStyles.label}>Quantidade:</Text>
                    <Text style={pdfStyles.value}>{String(data.quantity ?? 'N/A')}</Text>
                </View>
            </View>

            <View style={pdfStyles.section}>
                <Text style={pdfStyles.sectionTitle}>Descrição</Text>
                <View style={pdfStyles.textBlock}>
                    <Text>{data.description || 'Sem descricao.'}</Text>
                </View>
            </View>

            <View style={pdfStyles.section}>
                <Text style={pdfStyles.sectionTitle}>Causa e correção</Text>
                <View style={pdfStyles.row}>
                    <Text style={pdfStyles.label}>Causa:</Text>
                    <Text style={pdfStyles.value}>{data.cause || 'Nao informada.'}</Text>
                </View>
                <View style={pdfStyles.row}>
                    <Text style={pdfStyles.label}>Correção:</Text>
                    <Text style={pdfStyles.value}>{data.correction || 'Nao informada.'}</Text>
                </View>
            </View>

            <View style={pdfStyles.section}>
                <Text style={pdfStyles.sectionTitle}>Providências</Text>
                {providences.length > 0 ? (
                    providences.map((providence, index) => (
                        <Text key={`${providence.description}-${index}`} style={pdfStyles.listItem}>
                            {`${index + 1}. ${providence.description || '-'} | Responsavel: ${providence.responsibleName || '-'} | Prazo: ${formatDate(providence.deadline)}`}
                        </Text>
                    ))
                ) : (
                    <Text>Nenhuma providência registrada.</Text>
                )}
            </View>
        </Page>
        {files.map((file, index) => (
            <Page key={`attachment-${file.id}-${index}`} size="A4" style={pdfStyles.page}>
                <Text style={pdfStyles.attachmentPageTitle}>{`Anexo ${index + 1}`}</Text>
                {file.imageDataUrl ? (
                    <Image src={file.imageDataUrl} style={pdfStyles.attachmentImage} />
                ) : (
                    <Text>
                        Pré-visualização indisponível neste formato (ou não foi possível carregar a imagem). O
                        anexo permanece registrado na ocorrência.
                    </Text>
                )}
            </Page>
        ))}
    </Document>
)

const ViewOccurrenceModal: React.FC = () => {
    const { setModalActive } = useContext(ModalContext)
    const { occurrence } = useContext(OccurrenceContext)
    const { setFeedback } = useContext(FeedbackContext)

    const [fullData, setFullData] = useState<Occurrence | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [pdfFiles, setPdfFiles] = useState<Array<{ id: number; name: string; url?: string; imageDataUrl?: string }>>([])
    const [showPdfConfirm, setShowPdfConfirm] = useState(false)
    const [includePdfAttachments, setIncludePdfAttachments] = useState(true)
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)

    useEffect(() => {
        const loadData = async () => {
            if (!occurrence?.id) {
                setIsLoading(false)
                return
            }

            try {
                const response = await ApiService.occurrences.findById(occurrence.id)
                setFullData(response.data)
            } catch (error) {
                console.error('Erro ao carregar ocorrência:', error)
                setFullData(occurrence)
            } finally {
                setIsLoading(false)
            }
        }

        loadData()
    }, [occurrence?.id])

    const data = fullData || occurrence

    useEffect(() => {
        const current = fullData || occurrence
        if (!current?.id) {
            setPdfFiles([])
            return
        }

        const filesFromOccurrence = (current.FileInOccurrence || []).map(f => ({
            id: f.GeneralFile.id,
            name: f.GeneralFile.title,
            url: f.GeneralFile.fileUrl,
        }))

        if (filesFromOccurrence.length === 0) {
            setPdfFiles([])
            return
        }

        let cancelled = false

        const resolvePdfFileUrls = async () => {
            try {
                const resolved = await Promise.all(
                    filesFromOccurrence.map(async file => {
                        try {
                            const response = await ApiService.occurrences.downloadFile(current.id!, file.id)
                            const payload = response?.data as { url?: string } | undefined
                            const resolvedUrl = payload?.url || file.url
                            let imageDataUrl: string | undefined
                            if (resolvedUrl) {
                                imageDataUrl = (await tryFetchImageAsDataUrl(resolvedUrl)) ?? undefined
                            }
                            if (!imageDataUrl) {
                                try {
                                    const contentRes = await ApiService.occurrences.getFileContent(
                                        current.id!,
                                        file.id
                                    )
                                    const body = contentRes?.data as { base64?: string; mimeType?: string } | undefined
                                    imageDataUrl = resolveImageDataUrlFromApiContent(
                                        body?.mimeType || '',
                                        body?.base64
                                    )
                                } catch {
                                    /* ignore */
                                }
                            }
                            return {
                                ...file,
                                url: resolvedUrl,
                                imageDataUrl,
                            }
                        } catch {
                            return file
                        }
                    })
                )
                if (!cancelled) {
                    setPdfFiles(resolved)
                }
            } catch {
                if (!cancelled) {
                    setPdfFiles(filesFromOccurrence)
                }
            }
        }

        resolvePdfFileUrls()

        return () => {
            cancelled = true
        }
    }, [fullData, occurrence])

    const handleFileDownload = async (file: { id: number; name: string }) => {
        if (!data?.id) return
        try {
            const response = await ApiService.occurrences.downloadFile(data.id, file.id)
            const { url } = response.data
            const link = document.createElement('a')
            link.href = url
            link.download = file.name
            link.style.display = 'none'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } catch (error) {
            console.error('Erro ao baixar arquivo:', error)
            setFeedback?.({
                isOpen: true,
                statusOk: false,
                message: 'Erro ao baixar o arquivo. Tente novamente.',
            })
        }
    }

    const handleGeneratePdf = async () => {
        if (!data?.id || isGeneratingPdf) return

        setIsGeneratingPdf(true)
        try {
            const pdfDocument = (
                <OccurrencePDFDocument
                    data={data}
                    providences={providences}
                    files={includePdfAttachments ? pdfFiles : []}
                />
            )
            const blob = await pdf(pdfDocument).toBlob()
            const url = URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = `ocorrencia_${data.id}.pdf`
            link.style.display = 'none'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            URL.revokeObjectURL(url)
            setShowPdfConfirm(false)
        } catch (error) {
            console.error('Erro ao gerar PDF:', error)
            setFeedback?.({
                isOpen: true,
                statusOk: false,
                message: 'Erro ao gerar PDF. Tente novamente.',
            })
        } finally {
            setIsGeneratingPdf(false)
        }
    }

    if (isLoading) {
        return (
            <Container>
                <div style={{ textAlign: 'center', padding: '2rem', color: '#8c98a4' }}>
                    Carregando dados da ocorrência...
                </div>
            </Container>
        )
    }

    if (!data) {
        return (
            <Container>
                <div style={{ textAlign: 'center', padding: '2rem', color: '#8c98a4' }}>
                    Nenhuma ocorrência selecionada.
                </div>
            </Container>
        )
    }

    const providences = [...(data.providences || [])].sort((a, b) => Number(b.id) - Number(a.id))

    const files = (data.FileInOccurrence || []).map(f => ({
        id: f.GeneralFile.id,
        name: f.GeneralFile.title,
        url: f.GeneralFile.fileUrl,
    }))

    return (
        <Container>
            <Header>
                <TitleRow>
                    <h2>Ocorrência #{data.id}</h2>
                    <RelevantBadge relevant={data.relevant}>
                        {data.relevant ? 'Relevante' : 'Não relevante'}
                    </RelevantBadge>
                </TitleRow>
                <CloseButton onClick={() => setModalActive(false)}>
                    <X size={16} />
                </CloseButton>
            </Header>

            <InfosContainer>
                {/* Informações Gerais */}
                <Section>
                    <SectionSubtitle>Informações gerais</SectionSubtitle>
                    <InfoGrid>
                        <InfoItem>
                            <InfoLabel>OP</InfoLabel>
                            <InfoValue>{data.operationNumber ?? 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Cliente</InfoLabel>
                            <InfoValue>{data.companyName || data.contactName || 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Área</InfoLabel>
                            <InfoValue>{data.area ?? 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Setor</InfoLabel>
                            <InfoValue>{data.sector ?? 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Alocação</InfoLabel>
                            <InfoValue>{data.allocation ?? 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Etapa</InfoLabel>
                            <InfoValue>{data.Stage?.name ?? 'N/A'}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Quantidade</InfoLabel>
                            <InfoValue>{data.quantity ?? 'N/A'}</InfoValue>
                        </InfoItem>
                    </InfoGrid>
                </Section>

                <SectionDivider />

                {/* Descrição */}
                <Section>
                    <SectionSubtitle>Descrição</SectionSubtitle>
                    <TextBlock>{data.description || 'Sem descrição.'}</TextBlock>
                </Section>

                <SectionDivider />

                {/* Causa e Correção */}
                <Section>
                    <SectionSubtitle>Causa e Correção</SectionSubtitle>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div>
                            <TextBlockLabel>Causa</TextBlockLabel>
                            <TextBlock>{data.cause || 'Não informada.'}</TextBlock>
                        </div>
                        <div>
                            <TextBlockLabel>Correção</TextBlockLabel>
                            <TextBlock>{data.correction || 'Não informada.'}</TextBlock>
                        </div>
                    </div>
                </Section>

                <SectionDivider />

                {/* Providências */}
                <Section>
                    <SectionSubtitle>Providências</SectionSubtitle>
                    {providences.length > 0 ? (
                        <SimpleTable>
                            <thead>
                                <tr>
                                    <TableHeader>Descrição</TableHeader>
                                    <TableHeader style={{ width: '180px' }}>Responsável</TableHeader>
                                    <TableHeader style={{ textAlign: 'center', width: '120px' }}>Prazo</TableHeader>
                                </tr>
                            </thead>
                            <tbody>
                                {providences.map((providence, index) => (
                                    <SimpleTableRow key={providence.id || index}>
                                        <TableCell>{providence.description || '-'}</TableCell>
                                        <TableCell>{providence.responsibleName || '-'}</TableCell>
                                        <TableCell style={{ textAlign: 'center' }}>
                                            {formatDate(providence.deadline)}
                                        </TableCell>
                                    </SimpleTableRow>
                                ))}
                            </tbody>
                        </SimpleTable>
                    ) : (
                        <p style={{ textAlign: 'center', color: '#666', padding: '24px 0' }}>
                            Nenhuma providência registrada.
                        </p>
                    )}
                </Section>

                {/* Anexos */}
                {files.length > 0 && (
                    <>
                        <SectionDivider />
                        <Section>
                            <SectionSubtitle>Anexos</SectionSubtitle>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {files.map(file => (
                                    <div
                                        key={file.id}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '10px 14px',
                                            backgroundColor: '#FAFAFA',
                                            borderRadius: '6px',
                                            fontSize: '14px',
                                            color: '#262626',
                                        }}
                                    >
                                        <span style={{ flex: 1, wordBreak: 'break-word', marginRight: '12px' }}>
                                            {file.name}
                                        </span>
                                        <span
                                            onClick={() => handleFileDownload(file)}
                                            style={{
                                                color: '#0090d3',
                                                fontSize: '13px',
                                                cursor: 'pointer',
                                                fontWeight: 500,
                                                flexShrink: 0,
                                            }}
                                        >
                                            Download
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </Section>
                    </>
                )}
            </InfosContainer>

            <ButtonContainer>
                <ButtonDiv
                    variant="primary"
                    onClick={() => {
                        setIncludePdfAttachments(true)
                        setShowPdfConfirm(true)
                    }}
                >
                    <Download size={14} />
                    Gerar PDF
                </ButtonDiv>
                <ButtonDiv
                    variant="ghost"
                    onClick={() => setModalActive(false)}
                    style={{ color: '#0090d3', fontWeight: 'bold' }}
                >
                    Fechar
                </ButtonDiv>
            </ButtonContainer>
            {showPdfConfirm && (
                <ConfirmOverlay>
                    <ConfirmCard>
                        <ConfirmTitle>Gerar PDF</ConfirmTitle>
                        <ConfirmText>Escolha se deseja incluir os anexos no documento.</ConfirmText>
                        <ConfirmOption>
                            <input
                                type="checkbox"
                                checked={includePdfAttachments}
                                onChange={(e) => setIncludePdfAttachments(e.target.checked)}
                            />
                            Incluir Anexos
                        </ConfirmOption>
                        <ConfirmActions>
                            <ButtonDiv
                                variant="ghost"
                                onClick={() => setShowPdfConfirm(false)}
                                disabled={isGeneratingPdf}
                            >
                                Cancelar
                            </ButtonDiv>
                            <ButtonDiv
                                variant="primary"
                                onClick={handleGeneratePdf}
                                disabled={isGeneratingPdf}
                            >
                                {isGeneratingPdf ? 'Gerando...' : 'Gerar PDF'}
                            </ButtonDiv>
                        </ConfirmActions>
                    </ConfirmCard>
                </ConfirmOverlay>
            )}
        </Container>
    )
}

export default ViewOccurrenceModal
